import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbToolbarContext } from "abreact/layout/toolbar/AbToolbar";
import { AbChiclet } from "abreact/misc/AbChiclet";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import * as AbHooks from "abreact/utils/AbHooks";
import { useSelectionGroupHandlers } from "abreact/utils/AbSelectGroup";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbIterable } from "abweb/collection/AbIterable";
import { AbKeySet } from "abweb/collection/AbKeySet";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";
export const AbChicletGroup = props => {
  const [selectedItems, setSelectedItems] = React.useState(AbKeySet.from([], props.keyFunction));
  const divRef = React.useRef(null);
  const itemArrayRef = AbHooks.useLazyRef(() => [...props.items]);
  itemArrayRef.current = [...props.items];
  const valueSet = AbKeySet.from(Array.from(props.items, AbEnriched.getItem), props.keyFunction); // Calculate on every render because value set contents may change without props.value changing.
  const [currFocusedIndex, setCurrFocusedIndex] = React.useState(0);
  const inToolbar = React.useContext(AbToolbarContext);
  const focusIndex = index => {
    const element = divRef.current?.querySelector(`.ab-chiclet-group > .ab-chiclet:nth-child(${index + 1})`) ?? undefined;
    FocusUtils.setFocus(element);
    if (element) setCurrFocusedIndex(index);
  };
  const helpers = useSelectionGroupHandlers({
    focusIndexFunction: focusIndex,
    items: AbListHelper.from([...props.items]),
    keyFunction: props.keyFunction,
    disabled: props.disabled,
    selectionModeProps: {
      selectionMode: SelectionMode.MULTIPLE,
      value: selectedItems.toSet(),
      onChange: newSet => {
        setSelectedItems(AbKeySet.from(newSet, props.keyFunction));
      },
      selectionConfig: {
        selectionMode: "few",
        selectionFollowsFocus: true
      }
    },
    orientation: "horizontal"
  });
  React.useImperativeHandle(props.abRef, () => ({
    focus: index => index !== undefined && focusIndex(index)
  }));
  const itemRenderer = props.itemRenderer ?? AbItemUtils.defaultItemDisplayName;
  const renderItem = (enrichedItem, index) => {
    const {
      item,
      enrichment
    } = AbEnriched.extract(enrichedItem);
    const disabled = (props.disabled || enrichment?.disabled) ?? false;
    const selected = selectedItems.has(item);
    return /*#__PURE__*/React.createElement(AbChiclet, {
      key: TypeUtils.itemToKey(item, props.keyFunction),
      label: itemRenderer(item, enrichment),
      style: props.style,
      className: ReactUtils.classNamesString([selected && "ab-selected", enrichment?.className]),
      tooltip: enrichment?.tooltip,
      disabled: disabled,
      onClick: e => {
        helpers.onClick(e, index);
      },
      onClose: disabled ? undefined : () => {
        props.onClose && props.onClose(item);
        const maybeEnrichedValueSet = AbKeySet.from(props.items, AbEnriched.createMaybeEnrichedKeyFunction(props.keyFunction));
        // only call onChange if there is not an onClose provided in the props
        !props.onClose && props.onChange(AbIterable.from(valueSet.difference([item])), AbIterable.from(maybeEnrichedValueSet.difference([enrichedItem])));
        window.requestAnimationFrame(() => {
          focusIndex(index);
        });
      },
      eventHandlers: {
        onBlur: e => {
          if (e.relatedTarget && e.relatedTarget?.parentElement === divRef.current) return;
          setSelectedItems(AbKeySet.from([], props.keyFunction));
        },
        onKeyDown: e => {
          if (e.key === KeyValue.ARROW_LEFT && index === 0 && inToolbar) return;
          if (e.key === KeyValue.ARROW_RIGHT && index === valueSet.size - 1 && inToolbar) return;
          helpers.onKeyDown(e, index);
        }
      },
      tabIndex: index === currFocusedIndex && !props.disabled ? props.tabIndex : -1
    });
  };
  const {
    disabled,
    tabIndex,
    ...rest
  } = ReactUtils.inputProps("ab-chiclet-group", props);
  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    ref: divRef,
    onMouseDown: e => {
      e.preventDefault(); // Prevents unwanted highlighting of text etc.
    }
  }), Array.from(props.items, renderItem));
};
//# sourceMappingURL=AbChicletGroup.js.map
